import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Block as BanIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { DrawRequest } from '@models/drawRequests/drawRequest';
import FormattedDate from '@helpers/formattedDate';
import FormattedCurrency from '@helpers/formattedCurrency';
import { useDrawRequestCommandApi } from '@hooks/api/useDrawRequestCommandApi';
import { toast } from 'react-hot-toast';
import { DRAW_REQUEST_STATUS } from '@constants/drawManagerConstants';
import DeleteConfirmationModal from '@components/sharedComponents/DeleteConfirmationModal';

interface DrawRequestCardProps {
  drawRequest: DrawRequest;
  onViewDrawRequest: (drawRequest: DrawRequest) => void;
  getStatusChipColor: (
    status: number,
    theme: any
  ) => {
    label: string;
    bgColor: string;
    textColor: string;
    borderRadius: string;
  };
  refetch?: () => void;
  sourceRecId?: string;
}

export default function DrawRequestCard({
  drawRequest,
  onViewDrawRequest,
  getStatusChipColor,
  refetch,
  sourceRecId,
}: DrawRequestCardProps) {
  const theme = useTheme();
  const { label, bgColor, textColor, borderRadius } = getStatusChipColor(
    drawRequest.status,
    theme
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const open = Boolean(anchorEl);

  const deleteDrawRequestMutation =
    useDrawRequestCommandApi.useDeleteDrawRequest();
  const cancelDrawRequestMutation =
    useDrawRequestCommandApi.useCancelDrawRequest();

  const showDelete = drawRequest.status === DRAW_REQUEST_STATUS.NEW;
  const showCancel =
    drawRequest.status !== DRAW_REQUEST_STATUS.DISBURSED &&
    drawRequest.status !== DRAW_REQUEST_STATUS.CANCELLED;
  const showMenu = showDelete || showCancel;

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleMenuClose();
    }
  };

  const handleDelete = () => {
    handleMenuClose();
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteConfirmOpen(false);
    if (drawRequest.id && sourceRecId) {
      deleteDrawRequestMutation.mutate(
        {
          drawRequestId: drawRequest.id,
          sourceRecId,
        },
        {
          onSuccess: () => {
            toast.success('Draw request deleted successfully');
            if (refetch) {
              refetch();
            }
          },
          onError: error => {
            toast.error(error.message);
          },
        }
      );
    }
  };

  const handleCancel = () => {
    handleMenuClose();
    setCancelConfirmOpen(true);
  };

  const handleConfirmCancel = async () => {
    setCancelConfirmOpen(false);
    if (drawRequest.id && sourceRecId) {
      cancelDrawRequestMutation.mutate(
        {
          drawRequestId: drawRequest.id,
          sourceRecId,
        },
        {
          onSuccess: () => {
            toast.success('Draw request cancelled successfully');
            if (refetch) {
              refetch();
            }
          },
          onError: error => {
            toast.error(error.message);
          },
        }
      );
    }
  };

  const renderRequestId = () => (
    <Box>
      <Box
        sx={{
          fontSize: '12px',
          color: theme.palette.text.secondary,
          marginBottom: '4px',
        }}
      >
        Request ID
      </Box>
      <Box
        sx={{
          fontSize: '18px',
          fontWeight: 600,
          color: theme.palette.text.primary,
        }}
      >
        {drawRequest.drawRequestId}
      </Box>
    </Box>
  );

  const renderStatusChip = () => {
    const hasUnreadMessages = drawRequest.hasUnreadMessages ?? false;

    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <Box
          sx={{
            backgroundColor: bgColor,
            color: textColor,
            borderRadius: borderRadius,
            padding: '4px 12px',
            fontSize: '12px',
            fontWeight: 500,
          }}
          role="status"
          aria-label={`Status: ${label}${hasUnreadMessages ? ', has unread messages' : ''}`}
        >
          {label}
        </Box>
        {hasUnreadMessages && (
          <EmailIcon
            sx={{
              fontSize: 14,
              color: theme.palette.primary.main,
              position: 'absolute',
              top: -6,
              right: -8,
            }}
            aria-label="Unread messages"
          />
        )}
      </Box>
    );
  };

  const renderHeaderActions = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {renderStatusChip()}
      {showMenu && (
        <Tooltip title="Actions" arrow>
          <IconButton
            size="small"
            onClick={handleMenuClick}
            aria-label={`More actions for draw request ${drawRequest.drawRequestId}`}
            aria-haspopup="true"
            aria-expanded={open}
            sx={{
              padding: '4px',
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const renderCardHeader = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '12px',
      }}
    >
      {renderRequestId()}
      {renderHeaderActions()}
    </Box>
  );

  const renderDivider = () => (
    <Box
      sx={{
        height: '1px',
        backgroundColor: theme.palette.divider,
        marginBottom: '12px',
      }}
    />
  );

  const renderDateSent = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
      }}
    >
      <Box sx={{ fontSize: '14px', color: theme.palette.text.secondary }}>
        Date Sent
      </Box>
      <Box
        sx={{
          fontSize: '14px',
          fontWeight: 500,
          color: theme.palette.text.primary,
        }}
        aria-label={`Date sent: ${new Date(drawRequest.dateCreated || new Date()).toLocaleDateString()}`}
      >
        <FormattedDate
          value={drawRequest.dateCreated || new Date()}
          showTime={false}
          options={{
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
          }}
        />
      </Box>
    </Box>
  );

  const renderBudgetItems = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Box sx={{ fontSize: '14px', color: theme.palette.text.secondary }}>
        Budget Items
      </Box>
      <Box
        sx={{
          fontSize: '14px',
          fontWeight: 500,
          color: theme.palette.text.primary,
        }}
        aria-label={`Budget amount: ${drawRequest.budgetAmount}`}
      >
        <FormattedCurrency value={drawRequest.budgetAmount} />
      </Box>
    </Box>
  );

  const renderDetailsSection = () => (
    <Box sx={{ marginBottom: '12px' }}>
      {renderDateSent()}
      {renderBudgetItems()}
    </Box>
  );

  const renderRequestAmount = () => (
    <Box sx={{ flex: 1, textAlign: 'center' }}>
      <Box
        sx={{
          fontSize: '12px',
          color: theme.palette.text.secondary,
          marginBottom: '4px',
        }}
      >
        Request
      </Box>
      <Box
        sx={{
          fontSize: '16px',
          fontWeight: 600,
          color: theme.palette.text.primary,
        }}
        aria-label={`Request amount: ${drawRequest.requestAmount}`}
      >
        <FormattedCurrency value={drawRequest.requestAmount} />
      </Box>
    </Box>
  );

  const renderBudgetedAmount = () => (
    <Box sx={{ flex: 1, textAlign: 'center' }}>
      <Box
        sx={{
          fontSize: '12px',
          color: theme.palette.text.secondary,
          marginBottom: '4px',
        }}
      >
        Budgeted
      </Box>
      <Box
        sx={{
          fontSize: '16px',
          fontWeight: 600,
          color: theme.palette.text.primary,
        }}
        aria-label={`Budgeted amount: ${drawRequest.budgetAmount}`}
      >
        <FormattedCurrency value={drawRequest.budgetAmount} />
      </Box>
    </Box>
  );

  const renderAmountsSection = () => (
    <Box
      sx={{
        backgroundColor: theme.palette.grey[50],
        padding: '12px',
        marginBottom: '16px',
        display: 'flex',
        gap: '12px',
      }}
    >
      {renderRequestAmount()}
      <Box sx={{ width: '1px', backgroundColor: theme.palette.divider }} />
      {renderBudgetedAmount()}
    </Box>
  );

  const renderViewButton = () => (
    <Button
      fullWidth
      variant="contained"
      onClick={() => onViewDrawRequest(drawRequest)}
      aria-label={`View details for draw request ${drawRequest.drawRequestId}`}
      sx={{
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        padding: '12px',
        fontSize: '14px',
        fontWeight: 500,
        textTransform: 'none',
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
        },
      }}
    >
      View Draw
    </Button>
  );

  const renderDeleteMenuItem = () => (
    <MenuItem
      onClick={handleDelete}
      aria-label={`Delete draw request ${drawRequest.drawRequestId}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: theme.palette.error.main,
        '&:hover': {
          backgroundColor: theme.palette.error.light + '20',
        },
        '&:focus': {
          outline: `2px solid ${theme.palette.error.main}`,
          outlineOffset: '2px',
        },
      }}
    >
      <DeleteIcon
        fontSize="small"
        aria-hidden="true"
        sx={{
          border: '1px solid',
          borderColor: 'error.main',
          borderRadius: '50%',
          padding: '2px',
        }}
      />
      Delete
    </MenuItem>
  );

  const renderCancelMenuItem = () => (
    <MenuItem
      onClick={handleCancel}
      aria-label={`Cancel draw request ${drawRequest.drawRequestId}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: theme.palette.primary.main,
        '&:hover': {
          backgroundColor: theme.palette.primary.light + '20',
        },
        '&:focus': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px',
        },
      }}
    >
      <BanIcon
        fontSize="small"
        aria-hidden="true"
        sx={{
          border: '1px solid',
          borderColor: 'primary.main',
          borderRadius: '50%',
          padding: '2px',
        }}
      />
      Cancel
    </MenuItem>
  );

  const renderActionsMenu = () => (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleMenuClose}
      onKeyDown={handleKeyDown}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          sx: {
            minWidth: 120,
            boxShadow: theme.shadows[4],
            border: `1px solid ${theme.palette.divider}`,
          },
        },
      }}
      role="menu"
      aria-label="Draw request actions"
    >
      {showDelete && renderDeleteMenuItem()}
      {showCancel && renderCancelMenuItem()}
    </Menu>
  );

  const renderCardContent = () => (
    <>
      <Box
        className="mobile-card"
        component="article"
        role="article"
        aria-label={`Draw request ${drawRequest.drawRequestId}`}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[1],
        }}
      >
        {renderCardHeader()}
        {renderDivider()}
        {renderDetailsSection()}
        {renderAmountsSection()}
        {renderViewButton()}
        {renderActionsMenu()}
      </Box>
      <DeleteConfirmationModal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Draw Request"
        message="You are about to delete this draw request. This action cannot be undone."
      />
      <DeleteConfirmationModal
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={handleConfirmCancel}
        title="Cancel Draw Request"
        message="You are about to cancel this draw request. This action cannot be undone."
        confirmButtonText="Cancel"
        closeButtonText="Close"
      />
    </>
  );

  return renderCardContent();
}
